"use client";

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Heart } from "lucide-react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import registerImage from "../../assets/images/register.png";
import apiEndpoints from "../../apiconfig/index.js";

export default function Register() {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "Admin",
    agreeToTerms: false,
    showPassword: false,
    showConfirmPassword: false,
    loading: false,
    error: null,
    success: false,
  });

  const [fieldErrors, setFieldErrors] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: "",
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load Montserrat font from Google Fonts
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const navigate = useNavigate();

  // Define font styles for consistency
  const fontStyles = {
    fontFamily: "'Montserrat', sans-serif",
  };

  const validateField = (fieldName, value) => {
    let error = "";

    switch (fieldName) {
      case "username":
        if (!value.trim()) {
          error = "Please enter your name";
        }
        break;
      case "email":
        if (!value.trim()) {
          error = "Please enter your email address";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "password":
        if (!value) {
          error = "Please enter a password";
        } else if (value.length < 8) {
          error = "Password must be at least 8 characters long";
        } else if (
          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#])/.test(value)
        ) {
          error =
            "Password must include uppercase, lowercase, number, and special character";
        }
        break;
      case "confirmPassword":
        if (!value) {
          error = "Please confirm your password";
        } else if (value !== formData.password) {
          error = "Passwords do not match";
        }
        break;
      case "agreeToTerms":
        if (!value) {
          error = "Please agree to the terms";
        }
        break;
      default:
        break;
    }

    setFieldErrors((prev) => ({ ...prev, [fieldName]: error }));
    return error === "";
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleBlur = (field, value) => {
    validateField(field, value);
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...fieldErrors };

    // Validate each field
    if (!validateField("username", formData.username)) isValid = false;
    if (!validateField("email", formData.email)) isValid = false;
    if (!validateField("password", formData.password)) isValid = false;
    if (!validateField("confirmPassword", formData.confirmPassword))
      isValid = false;

    return isValid;
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const res = await fetch(apiEndpoints.register, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "register",
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        }),
      });

      const result = await res.json();

      // Check for status instead of success
      if (result.status === "success") {
        setFormData((prev) => ({ ...prev, loading: false }));

        // Show success snackbar
        setSnackbar({
          open: true,
          message: "Registration successful! Redirecting to login...",
          severity: "success",
        });

        // Navigate after a delay to allow user to see the snackbar
        setTimeout(() => {
          setFormData((prev) => ({ ...prev, success: true }));
          navigate("/");
        }, 2000);
      } else {
        setFormData((prev) => ({
          ...prev,
          loading: false,
          error: result.message || "Registration failed",
        }));

        // Show error snackbar
        setSnackbar({
          open: true,
          message: result.message || "Registration failed",
          severity: "error",
        });
      }
    } catch (err) {
      setFormData((prev) => ({
        ...prev,
        loading: false,
        error: "Server error. Please try again later.",
      }));

      // Show error snackbar
      setSnackbar({
        open: true,
        message: "Server error. Please try again later.",
        severity: "error",
      });
    }
  };

  if (formData.success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f8f9fb",
          p: 2,
          ...fontStyles,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            maxWidth: 450,
            width: "100%",
            borderRadius: 4,
            ...fontStyles,
          }}
        >
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                width: 64,
                height: 64,
                bgcolor: "#c1c1c1ff",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Heart style={{ color: "white", width: 32, height: 32 }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              color="text.primary"
              sx={fontStyles}
            >
              Registration Successful!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              mb={2}
              sx={fontStyles}
            >
              Your admin account has been created successfully.
            </Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            sx={{
              height: 44,
              backgroundColor: "black",
              ...fontStyles,
            }}
            onClick={() => navigate("/")}
          >
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f8f9fb",
          p: { xs: 1, sm: 2 },
          overflow: "hidden",
          ...fontStyles,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            width: "100%",
            maxWidth: 1100,
            height: "100%",
            maxHeight: 700,
            p: 4,
            borderRadius: 4,
            boxShadow: "1 1 1 rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            ...fontStyles,
          }}
        >
          {/* Left side - Registration Form */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              height: "100%",
              pr: { md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              overflowY: "auto",
              maxHeight: 700,
              ...fontStyles,
              scrollbarWidth: "none", // Firefox
              msOverflowStyle: "none", // IE / Edge
              "&::-webkit-scrollbar": {
                display: "none", // Chrome / Safari
              },
            }}
          >
            <Box textAlign="center" mb={3}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "#c1c1c1ff",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Heart style={{ color: "Black", width: 32, height: 32 }} />
              </Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                color="text.primary"
                sx={{
                  fontSize: { xs: "22px" },
                  ...fontStyles,
                }}
              >
                Create Admin Account
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ marginTop: 8 }}
                sx={{
                  fontSize: { xs: "12px" },
                  ...fontStyles,
                }}
              >
                Register as a new admin user
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                label="User Name"
                value={formData.username}
                onChange={(e) => handleInputChange("username", e.target.value)}
                onBlur={(e) => handleBlur("username", e.target.value)}
                error={!!fieldErrors.username}
                helperText={fieldErrors.username}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                  sx: fontStyles,
                }}
                InputLabelProps={{
                  sx: fontStyles,
                }}
                FormHelperTextProps={{
                  sx: { color: "red", ...fontStyles },
                }}
              />

              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onBlur={(e) => handleBlur("email", e.target.value)}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: fontStyles,
                }}
                InputLabelProps={{
                  sx: fontStyles,
                }}
                FormHelperTextProps={{
                  sx: { color: "red", ...fontStyles },
                }}
              />

              <Box position="relative" mb={2}>
                <TextField
                  label="Password"
                  type={formData.showPassword ? "text" : "password"}
                  fullWidth
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  onBlur={(e) => handleBlur("password", e.target.value)}
                  error={!!fieldErrors.password}
                  helperText={fieldErrors.password}
                  InputProps={{
                    sx: fontStyles,
                  }}
                  InputLabelProps={{
                    sx: fontStyles,
                  }}
                  FormHelperTextProps={{
                    sx: { color: "red", ...fontStyles },
                  }}
                />
                <IconButton
                  onClick={() =>
                    handleInputChange("showPassword", !formData.showPassword)
                  }
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {formData.showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </IconButton>
              </Box>

              <Box position="relative" mb={2}>
                <TextField
                  label="Confirm Password"
                  type={formData.showConfirmPassword ? "text" : "password"}
                  fullWidth
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    handleInputChange("confirmPassword", e.target.value)
                  }
                  onBlur={(e) => handleBlur("confirmPassword", e.target.value)}
                  error={!!fieldErrors.confirmPassword}
                  helperText={fieldErrors.confirmPassword}
                  InputProps={{
                    sx: fontStyles,
                  }}
                  InputLabelProps={{
                    sx: fontStyles,
                  }}
                  FormHelperTextProps={{
                    sx: { color: "red", ...fontStyles },
                  }}
                />
                <IconButton
                  onClick={() =>
                    handleInputChange(
                      "showConfirmPassword",
                      !formData.showConfirmPassword,
                    )
                  }
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {formData.showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </IconButton>
              </Box>

              {formData.error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    mb: 1,
                    ...fontStyles,
                  }}
                >
                  {formData.error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={formData.loading}
                sx={{
                  height: 44,
                  mb: 2,
                  backgroundColor: "black",
                  ...fontStyles,
                }}
              >
                {formData.loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" align="center" sx={fontStyles}>
              Already have an account?{" "}
              <Typography
                component={Link}
                to="/"
                sx={{
                  color: "black",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                  ...fontStyles,
                }}
              >
                Sign in
              </Typography>
            </Typography>
          </Box>

          {/* Right side - Image */}
          <Box
            component="img"
            src={registerImage}
            alt="Registration illustration"
            sx={{
              width: { xs: "100%", md: "50%" },
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              objectFit: "fill",
              borderRadius: 2,
            }}
          />
        </Paper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }} // Changed from 'bottom', 'center'
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", ...fontStyles }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
